import { Component, OnInit } from "@angular/core";
import { Connection, RoleInOrganisation } from "@common/ADAPT.Common.Model/organisation/connection";
import { Organisation } from "@common/ADAPT.Common.Model/organisation/organisation";
import { RoleTypeCode } from "@common/ADAPT.Common.Model/organisation/role-type-code";
import { Person } from "@common/ADAPT.Common.Model/person/person";
import { IAddPeopleBindingModel } from "@common/identity/add-people-binding-model.interface";
import { IdentityService } from "@common/identity/identity.service";
import { CommonDataService } from "@common/lib/data/common-data.service";
import { ErrorHandlingUtilities } from "@common/lib/utilities/error-handling-utilities";
import { UserService } from "@common/user/user.service";
import { DirectorySharedService } from "@org-common/lib/directory-shared/directory-shared.service";
import { OrganisationService } from "@org-common/lib/organisation/organisation.service";
import { CommonTeamsService } from "@org-common/lib/teams/common-teams.service";
import { WorkflowService } from "@org-common/lib/workflow/workflow.service";
import { WorkflowStepComponent } from "@org-common/lib/workflow/workflow-component-registry";
import { WorkflowIdentifier } from "@org-common/lib/workflow/workflow-identifier.enum";
import { WorkflowStepComponentAdapter } from "@org-common/lib/workflow/workflow-step-component-adapter";
import { BehaviorSubject, catchError, lastValueFrom, map, of, switchMap } from "rxjs";
import { Workflows } from "../../workflows";

@WorkflowStepComponent("adapt-invite-business-leaders-add-leadership-team")
@Component({
    selector: "adapt-invite-business-leaders-add-leadership-team",
    templateUrl: "./invite-business-leaders-add-leadership-team.component.html",
    standalone: false,
})
export class InviteBusinessLeadersAddLeadershipTeamComponent extends WorkflowStepComponentAdapter implements OnInit {
    public validationGroupValid = new BehaviorSubject<boolean>(false);
    public workflowStepCompleted = this.validationGroupValid.asObservable().pipe(
        switchMap((isValid) => isValid
            // if valid, cannot just use map((isValid) => isValid && this.hasOwner) as we will allow it if user tick the box
            ? this.skipOwnerTask.pipe(
                map((skipped) => skipped || this.hasOwner))
            : of(isValid)),
    );
    public skipOwnerTask = new BehaviorSubject<boolean>(false);
    public workflowStepSkipEnabled = this.skipOwnerTask.asObservable().pipe(
        map((skipped) => skipped || this.currentUserIsOwner),
    );

    public addPeopleModel?: IAddPeopleBindingModel;
    public organisation?: Organisation;
    public currentPerson: Person;
    public currentPersonConnection: Connection;
    public includeCustomMessage = false;

    private currentUserIsOwner = false;
    private hasOwnersInOrg = false;

    public constructor(
        private identityService: IdentityService,
        private commonTeamsService: CommonTeamsService,
        private workflowService: WorkflowService,
        private orgService: OrganisationService,
        private commonDataService: CommonDataService,
        private commonDirectoryService: DirectorySharedService,
        userService: UserService,
    ) {
        super();

        this.currentPerson = userService.currentPerson!;
        this.currentPersonConnection = this.currentPerson.getLatestConnection()!;
    }

    public get hasOwner() {
        return this.currentUserIsOwner ||
            this.hasOwnersInOrg ||
            !!this.addPeopleModel?.userDetails.some((userDetail) => userDetail.roleInOrganisation === RoleInOrganisation.OwnerLeader);
    }

    public async ngOnInit() {
        this.organisation = await lastValueFrom(this.orgService.getOrganisationWithSupplementaryData(this.orgService.getOrganisationId()));
        this.updateSkippable();
    }

    public async currentPersonRoleChanged(role: RoleInOrganisation) {
        this.currentPersonConnection.roleInOrganisation = role;
        await lastValueFrom(this.commonDataService.saveEntities(this.currentPersonConnection));
        this.updateSkippable();
    }

    public addPeopleBindingModelChanged(bindingModel: IAddPeopleBindingModel) {
        this.addPeopleModel = bindingModel;
        this.updateSkippable();
    }

    public postWorkflow() {
        // assign the understand journey workflow to the user so it shows in their active pathways
        const understandJourneyWorkflow = Workflows[WorkflowIdentifier.UnderstandJourney];
        return this.workflowService.getLatestStatusForWorkflow(understandJourneyWorkflow).pipe(
            // only create if there isn't an existing connection/status
            switchMap((workflowStatus) => workflowStatus
                ? of(undefined)
                : this.workflowService.createWorkflowConnectionForWorkflow(understandJourneyWorkflow)),
            switchMap(() => this.hasOwner
                ? of(undefined)
                : this.skipOwnersPathways()),
        );
    }

    public workflowStepNext() {
        // save only the supplementaryData since that is the only thing that is being modified in this step
        return this.commonDataService.saveEntities(this.organisation?.supplementaryData).pipe(
            switchMap(async () => {
                const leadershipTeam = await this.commonTeamsService.promiseToGetLeadershipTeam();
                const role = await this.commonTeamsService.promiseToGetTeamRoleByRoleTypeCode(leadershipTeam.teamId, RoleTypeCode.TeamMember);
                if (!role) {
                    throw new Error("Team role failed to fetch");
                }

                // add leaders to leadership team (as team member)
                for (const userDetail of this.addPeopleModel!.userDetails) {
                    if ((userDetail.roleInOrganisation === RoleInOrganisation.Other)
                        || (userDetail.roleInOrganisation === RoleInOrganisation.Employee)) {
                        continue;
                    }

                    userDetail.roleIds = (userDetail.roleIds ?? []).concat(role.roleId);
                }

                await this.identityService.addPeople(this.addPeopleModel!);

                // update all people so added people will appear in the platform
                await this.commonDirectoryService.promiseToGetAllPeople(true);
            }),
            catchError((error) => {
                const errorMessage = ErrorHandlingUtilities.getHttpResponseMessage(error);
                this.workflowStepErrorMessage.next(errorMessage);
                throw error;
            }),
        );
    }

    private updateSkippable() {
        this.currentUserIsOwner = this.currentPersonConnection.roleInOrganisation === RoleInOrganisation.OwnerLeader;
        this.hasOwnersInOrg = this.organisation?.connections.some((c) => c.roleInOrganisation === RoleInOrganisation.OwnerLeader) ?? false;
        this.skipOwnerTask.next(false);
    }

    private async skipOwnersPathways() {
        // only complete 1 at a time as both workflows will enable PurposeAndVision feature - will be race condition if done in
        // a forkJoin
        await this.workflowService.completeWorkflow(Workflows[WorkflowIdentifier.DefineOwnersWhy]);
        await this.workflowService.completeWorkflow(Workflows[WorkflowIdentifier.AlignOwnersVision]);
    }
}
